import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, ButtonProps, Paper, Stack, Grid } from '@mui/material';
import i18n from 'app-i18n';
import { IncFormHelperTextProps } from 'components/forms/inc-form-helper-text/inc-form-helper-text.component';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AnyObjectSchema, object, string } from 'yup';
import IncTextField, {
  IncTextFieldProps,
} from '../inc-text-field/inc-text-field.component';
import KeyValueItem from './key-value-item.component';

export type KeyValueFieldProps = {
  name: string;
  keyProps?: Partial<IncTextFieldProps>;
  valueProps?: Partial<IncTextFieldProps>;
  addProps?: Partial<ButtonProps>;
  helperText?: string;
  IncFormHelperTextProps?: IncFormHelperTextProps;
  schema?: AnyObjectSchema;
  filterKeys?: string[];
  elevation?: number;
  variant?: 'standard' | 'drawer';
};

export const keySchema = string()
  .required()
  .max(1000, () => i18n.t('forms.fields.keyValueField.key.errors.max'))
  .matches(/^[a-zA-Z0-9_-]*$/, () =>
    i18n.t('forms.fields.keyValueField.key.errors.matches'),
  )
  .default('');

export const valueSchema = string()
  .required()
  .max(1000, () => i18n.t('forms.fields.keyValueField.value.errors.max'))
  .default('');

export const keyValueSchema = object({
  key: keySchema
    .test(
      'no-dupes',
      () => i18n.t('forms.fields.keyValueField.key.errors.no-dupes'),
      (value, context) => !context.options.context.keys?.includes(value),
    )
    .default(''),
  value: valueSchema,
});

export const KeyValueField = ({
  name,
  keyProps,
  valueProps,
  addProps,
  helperText,
  IncFormHelperTextProps,
  schema,
  filterKeys,
  elevation = 3,
  variant = 'standard',
  ...rest
}: KeyValueFieldProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({ name });
  const keys = useWatch({ name })?.map(entry => entry.key) ?? [];
  const finalSchema = schema ?? keyValueSchema;

  const form = useForm({
    resolver: yupResolver(finalSchema),
    defaultValues: finalSchema.cast({}),
    context: {
      keys,
    },
  });

  const add = data => {
    append(data);
  };

  const keyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();
      form.handleSubmit(add)();
    }
  };

  return (
    <Stack spacing={2} {...rest}>
      <Paper sx={{ p: 2 }} elevation={elevation}>
        <FormProvider {...form}>
          <Stack
            justifyContent="space-between"
            spacing={2}
            direction={{
              xs: 'column',
              md: variant === 'drawer' ? 'column' : 'row',
            }}
          >
            <Stack
              spacing={1}
              direction={{
                xs: 'column',
                md: variant === 'drawer' ? 'column' : 'row',
              }}
            >
              <IncTextField
                label={t('forms.fields.keyValueField.key.label')}
                margin="none"
                sx={{ minWidth: 230 }}
                helperText={helperText}
                IncFormHelperTextProps={IncFormHelperTextProps}
                {...keyProps}
                onKeyDown={keyDownHandler}
                name="key"
              />
              <IncTextField
                label={t('forms.fields.keyValueField.value.label')}
                margin="none"
                sx={{ minWidth: 230 }}
                {...valueProps}
                onKeyDown={keyDownHandler}
                name="value"
              />
            </Stack>
            <Box>
              <Button
                color="primary"
                type="button"
                {...addProps}
                onClick={form.handleSubmit(add)}
              >
                {t('forms.fields.keyValueField.add.label')}
              </Button>
            </Box>
          </Stack>
        </FormProvider>
      </Paper>
      <Grid container columnSpacing={1} rowSpacing={2}>
        {fields.map(
          ({ id }, index) =>
            !filterKeys?.includes(keys[index]) && (
              <Grid
                key={id}
                size={{ xs: 12, md: variant === 'drawer' ? 12 : 6 }}
              >
                <KeyValueItem
                  name={`${name}.${index}`}
                  remove={() => remove(index)}
                />
              </Grid>
            ),
        )}
      </Grid>
    </Stack>
  );
};
export default KeyValueField;
